import React, { useCallback, useContext, useState } from 'react';
import { toast } from 'react-toastify';
import { ThemeContext } from 'styled-components';

import { IOrderData } from '@hooks/useCurrentOrder/models';

import Loading from '@components/Loading';
import Input from '@components/Input';
import Select from '@components/Select';
import SomaStoreApiGateway from 'gateways/SomaStoreApiGateway';
import {
  Container,
  Header,
  Title,
  FormRow,
  Legend,
  Footer,
  CancelButton,
  IntegrateButton,
} from './styles';

interface IProps {
  order: IOrderData;
  hide: () => any;
  refetch?: () => Promise<void>;
}

interface IFormData {
  selected_payment_method: string;
  acquirer: string;
  card_type: string;
  card_brand: string;
  amount: number;
  installment_quantity: string;
  authorization_code: string;
  acquirer_nsu: string;
}

const PaymentMethodOptions = [{ label: 'POS', value: 'pos' }];
const AcquirerTypeOptions = [
  { label: 'Stone', value: 'stone' },
  { label: 'Getnet', value: 'getnet' },
];
const CardTypeOptions = [
  { label: 'Crédito', value: 'credit' },
  { label: 'Débito', value: 'debit' },
];
const PortionTypeOptions = [
  { label: 'à vista', value: '1' },
  { label: '2', value: '2' },
  { label: '3', value: '3' },
  { label: '4', value: '4' },
  { label: '5', value: '5' },
  { label: '6', value: '6' },
  { label: '7', value: '7' },
  { label: '8', value: '8' },
  { label: '9', value: '9' },
  { label: '10', value: '10' },
  { label: '11', value: '11' },
  { label: '12', value: '12' },
];

function capitalizeFirstLetter(text: string) {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}
function normalizeCardBrand(brand: string) {
  if (!brand) return null;

  const normalized = brand.toLowerCase().replace(/[^a-z]/g, '');

  if (['amex', 'americanexpress', 'americanexpress'].includes(normalized)) {
    return 'americanexpress';
  }

  return normalized;
}

const ManualIntegrationModal: React.FC<IProps> = ({ order, hide, refetch }) => {
  const [formData, setFormData] = useState<IFormData>({
    selected_payment_method: PaymentMethodOptions[0].value,
    acquirer: '',
    card_type: '',
    card_brand: '',
    amount: 0,
    installment_quantity: '',
    authorization_code: '',
    acquirer_nsu: '',
  });
  const [loading, setLoading] = useState(false);

  const themeContext = useContext(ThemeContext);

  const user_card_brands =
    order?.store.invoice_providers[0]?.provider.payload.code_table;

  const creditBrands = () => {
    const seen = new Set();
    if (!user_card_brands) {
      return [];
    }

    return user_card_brands
      .filter(item => item.card_type === 'credit' && item.card_brand !== null)
      .map(item => normalizeCardBrand(item.card_brand))
      .filter(brand => {
        if (seen.has(brand)) return false;
        seen.add(brand);
        return true;
      })
      .map(brand => ({
        label: capitalizeFirstLetter(brand || ''),
        value: brand || '',
      }));
  };

  const debitBrands = () => {
    const seen = new Set();
    if (!user_card_brands) {
      return [];
    }
    return user_card_brands
      .filter(item => item.card_type === 'debit' && item.card_brand !== null)
      .map(item => normalizeCardBrand(item.card_brand))
      .filter(brand => {
        if (seen.has(brand)) return false;
        seen.add(brand);
        return true;
      })
      .map(brand => ({
        label: capitalizeFirstLetter(brand || ''),
        value: brand || '',
      }));
  };

  const handleInputChange = useCallback(
    (field: keyof IFormData) => (value: string) => {
      setFormData(prev => ({ ...prev, [field]: value }));
    },
    [],
  );

  const handleSelectChange = useCallback(
    (field: keyof IFormData) => (value: string) => {
      setFormData(prev => ({ ...prev, [field]: value }));
    },
    [],
  );

  const formatCents = (cents: number): string => {
    if (cents === 0) return '';
    return (cents / 100).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handleValueChange = useCallback((value: string) => {
    const digits = value.replace(/\D/g, '');
    if (!digits) {
      setFormData(prev => ({ ...prev, amount: 0 }));
      return;
    }
    setFormData(prev => ({ ...prev, amount: parseInt(digits, 10) }));
  }, []);

  const isFormValid = useCallback(() => {
    const { amount, ...stringFields } = formData;
    return (
      Object.values(stringFields).every(v => String(v).trim() !== '') &&
      amount > 0
    );
  }, [formData]);

  // const handleRefresh = () => {
  //   window.location.reload();
  // };

  const handleIntegrate = async () => {
    if (!isFormValid()) {
      toast.error('Todos os campos são obrigatórios');
      return;
    }

    setLoading(true);

    try {
      await SomaStoreApiGateway.manualIntegration(order.id, [
        {
          amount: formData.amount,
          acquirer: formData.acquirer,
          selected_payment_method: formData.selected_payment_method,
          card_type: formData.card_type,
          card_brand: formData.card_brand,
          authorization_code: formData.authorization_code,
          acquirer_nsu: formData.acquirer_nsu,
          installment_quantity: parseInt(formData.installment_quantity, 10),
        },
      ]);
      toast.success('Integração concluída com sucesso!', { autoClose: 5000 });
      hide();
      if (refetch) await refetch();
    } catch (_error) {
      toast.error('Erro ao integrar cobrança');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Header>
        <Title>Integração manual de cobrança</Title>
      </Header>

      <FormRow>
        <Select
          disabled
          standalone
          label="Tipo de Pagamento"
          placeholder="Selecione o tipo de pagamento"
          options={PaymentMethodOptions}
          value={formData.selected_payment_method}
          onChange={handleSelectChange('selected_payment_method')}
          variant="outlined"
          size="small"
        />
        <Select
          standalone
          label="Adquirente"
          placeholder="Adquirente"
          options={AcquirerTypeOptions}
          value={formData.acquirer}
          onChange={handleSelectChange('acquirer')}
          variant="outlined"
          size="small"
        />
      </FormRow>

      <FormRow>
        <Select
          standalone
          label="Modo"
          placeholder="Modo"
          options={CardTypeOptions}
          value={formData.card_type}
          onChange={handleSelectChange('card_type')}
          variant="outlined"
          size="small"
        />
        <Select
          standalone
          label="Bandeira"
          placeholder="Bandeira"
          options={
            formData.card_type === 'credit' ? creditBrands() : debitBrands()
          }
          value={formData.card_brand}
          onChange={handleSelectChange('card_brand')}
          variant="outlined"
          size="small"
        />
      </FormRow>

      <FormRow>
        <Input
          standalone
          label="Valor"
          placeholder="0,00"
          startAdornment="R$"
          value={formatCents(formData.amount)}
          onChange={handleValueChange}
          onKeyPress={(e: React.KeyboardEvent) => {
            if (!/[0-9]/.test(e.key)) e.preventDefault();
          }}
          variant="outlined"
          size="small"
        />
        <Select
          standalone
          label="Parcelas"
          placeholder="Parcelas"
          options={PortionTypeOptions}
          value={formData.installment_quantity}
          onChange={handleSelectChange('installment_quantity')}
          variant="outlined"
          size="small"
        />
      </FormRow>

      <FormRow>
        <Input
          standalone
          label="Código de autorização"
          placeholder="Código de autorização"
          value={formData.authorization_code}
          onChange={handleInputChange('authorization_code')}
          variant="outlined"
          size="small"
        />
        <Input
          standalone
          label="NSU"
          placeholder="NSU"
          value={formData.acquirer_nsu}
          onChange={handleInputChange('acquirer_nsu')}
          variant="outlined"
          size="small"
        />
      </FormRow>

      <Legend>*Todos os campos são de preenchimento obrigatório</Legend>

      <Footer>
        <CancelButton variant="outlined" onClick={hide}>
          Cancelar
        </CancelButton>
        <IntegrateButton
          onClick={handleIntegrate}
          disabled={!isFormValid() || loading}
        >
          {loading ? (
            <Loading size={1.8} color={themeContext.font_low} />
          ) : (
            'Integrar Cobrança'
          )}
        </IntegrateButton>
      </Footer>
    </Container>
  );
};

export default ManualIntegrationModal;
